import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Radio,
  Select,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { ResidualRisk } from '../../../api/workEnvironmentTypes';
import { useDecideResidualRiskMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import {
  decisionText,
  draftErrors,
  ownerLabel,
  RESIDUAL_DECISIONS,
  ResidualChoices,
  residualDraft,
  ResidualDraft,
  ResidualField,
  residualFieldId,
  residualItemId,
  residualProgress,
  residualReasonText,
  toRequest,
} from '../archiveView';
import { today } from '../dataSourcesView';
import { LEVEL_COLORS } from '../riskView';
import { TextField } from '../components/FormParts';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from './Evaluation.module.css';

export const RESIDUAL_SECTION_ID = 'residual-risk';

type FormProps = {
  surveyId: number;
  risk: ResidualRisk;
  choices: ResidualChoices;
  onSaved: () => void;
};

const DecisionForm = ({
  surveyId,
  risk,
  choices,
  onSaved,
}: FormProps): ReactElement => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ResidualDraft>(
    residualDraft(risk.decision),
  );
  const [shown, setShown] = useState<ResidualField[]>([]);
  const decide = useDecideResidualRiskMutation(surveyId);
  const change = (next: Partial<ResidualDraft>) =>
    setDraft((current) => ({ ...current, ...next }));
  const errorFor = (field: ResidualField) =>
    shown.includes(field)
      ? t(`workEnvironmentArchive.residual.errors.${field}`)
      : undefined;

  const save = () => {
    const errors = draftErrors(draft, today());
    setShown(errors);
    if (errors.length === 0)
      decide.mutate(
        { factorKey: risk.key, decision: toRequest(draft) },
        { onSuccess: onSaved },
      );
  };

  return (
    <>
      <Fieldset data-size="sm" id={residualFieldId(risk.key, 'decision')}>
        <Fieldset.Legend>
          {t('workEnvironmentArchive.residual.question')}
        </Fieldset.Legend>
        <div className={classes.options}>
          {RESIDUAL_DECISIONS.map((option) => (
            <Radio
              key={option}
              name={`residual-${risk.key}`}
              value={option}
              label={t(`workEnvironmentArchive.residual.options.${option}`)}
              checked={draft.decision === option}
              aria-invalid={Boolean(errorFor('decision')) || undefined}
              onChange={() => change({ decision: option })}
            />
          ))}
        </div>
        {errorFor('decision') && (
          <ValidationMessage>{errorFor('decision')}</ValidationMessage>
        )}
      </Fieldset>
      {draft.decision === 'PLAN' && (
        <Paragraph data-size="sm" className={process.subtle}>
          {t('workEnvironmentArchive.residual.planText')}
        </Paragraph>
      )}
      {draft.decision === 'NEXT_SURVEY' && (
        <div className={classes.fields}>
          <Field data-size="sm">
            <Label htmlFor={residualFieldId(risk.key, 'nextSurveyBy')}>
              {t('workEnvironmentArchive.residual.nextSurveyBy')}
            </Label>
            <Input
              id={residualFieldId(risk.key, 'nextSurveyBy')}
              type="date"
              min={today()}
              value={draft.nextSurveyBy}
              aria-invalid={Boolean(errorFor('nextSurveyBy')) || undefined}
              onChange={(event) => change({ nextSurveyBy: event.target.value })}
            />
            {errorFor('nextSurveyBy') && (
              <ValidationMessage>{errorFor('nextSurveyBy')}</ValidationMessage>
            )}
          </Field>
          <Field data-size="sm">
            <Label htmlFor={residualFieldId(risk.key, 'owner')}>
              {t('workEnvironmentArchive.residual.owner')}
            </Label>
            <Select
              id={residualFieldId(risk.key, 'owner')}
              value={draft.ownerId ?? ''}
              aria-invalid={Boolean(errorFor('owner')) || undefined}
              onChange={(event) =>
                change({
                  ownerId: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
            >
              <Select.Option value="" disabled>
                {t('workEnvironmentArchive.residual.chooseOwner')}
              </Select.Option>
              {choices.owners.map((owner) => (
                <Select.Option key={owner.id} value={owner.id}>
                  {ownerLabel(owner, t)}
                </Select.Option>
              ))}
            </Select>
            {errorFor('owner') && (
              <ValidationMessage>{errorFor('owner')}</ValidationMessage>
            )}
          </Field>
        </div>
      )}
      {draft.decision === 'ACCEPT' && (
        <>
          <TextField
            id={residualFieldId(risk.key, 'reason')}
            label={t('workEnvironmentArchive.residual.reason')}
            help={t('workEnvironmentArchive.residual.reasonHelp')}
            value={draft.reason}
            error={errorFor('reason')}
            onChange={(reason) => change({ reason })}
          />
          <Field data-size="sm" className={classes.narrowField}>
            <Label htmlFor={residualFieldId(risk.key, 'acceptedBy')}>
              {t('workEnvironmentArchive.residual.acceptedBy')}
            </Label>
            <Select
              id={residualFieldId(risk.key, 'acceptedBy')}
              value={draft.acceptedBy ?? ''}
              aria-invalid={Boolean(errorFor('acceptedBy')) || undefined}
              onChange={(event) =>
                change({
                  acceptedBy:
                    choices.acceptedBy.find((body) => body === event.target.value) ??
                    null,
                })
              }
            >
              <Select.Option value="" disabled>
                {t('workEnvironmentArchive.residual.chooseAcceptedBy')}
              </Select.Option>
              {choices.acceptedBy.map((body) => (
                <Select.Option key={body} value={body}>
                  {t(
                    `workEnvironmentArchive.residual.acceptedByOptions.${body}`,
                  )}
                </Select.Option>
              ))}
            </Select>
            {errorFor('acceptedBy') && (
              <ValidationMessage>{errorFor('acceptedBy')}</ValidationMessage>
            )}
          </Field>
        </>
      )}
      {decide.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">
            {t('workEnvironmentArchive.residual.failed')}
          </Paragraph>
        </Alert>
      )}
      <div>
        <Button
          variant="secondary"
          data-size="sm"
          loading={decide.isPending}
          onClick={save}
        >
          {t('workEnvironmentArchive.residual.save')}
        </Button>
      </div>
    </>
  );
};

type ItemProps = {
  surveyId: number;
  risk: ResidualRisk;
  choices: ResidualChoices;
  canDecide: boolean;
};

const ResidualItem = ({
  surveyId,
  risk,
  choices,
  canDecide,
}: ItemProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(!risk.decision);
  const decision = risk.decision;

  return (
    <li id={residualItemId(risk.key)} className={classes.residualItem}>
      <div className={classes.residualTitle}>
        <span className={classes.focusTitle}>
          <span className="ds-label">{risk.name}</span>
          <Tag data-size="sm" data-color={LEVEL_COLORS[risk.level]}>
            {t(`workEnvironmentRisk.matrix.levels.${risk.level}`)}
          </Tag>
          {decision && (
            <Tag data-size="sm" data-color="success">
              {t('workEnvironmentArchive.residual.decidedTag')}
            </Tag>
          )}
        </span>
        <Paragraph data-size="sm" className={process.subtle}>
          {residualReasonText(risk, t)}
        </Paragraph>
      </div>
      {canDecide && editing ? (
        <DecisionForm
          surveyId={surveyId}
          risk={risk}
          choices={choices}
          onSaved={() => setEditing(false)}
        />
      ) : (
        decision && (
          <div className={classes.decided}>
            <Paragraph data-size="sm">
              {decisionText(
                { ...decision, owner: decision.owner && decision.owner.email },
                t,
                i18n.language,
              )}
            </Paragraph>
            {canDecide && (
              <div>
                <Button
                  variant="tertiary"
                  className={classes.inlineAction}
                  data-size="sm"
                  onClick={() => setEditing(true)}
                >
                  {t('workEnvironmentArchive.residual.change')}
                </Button>
              </div>
            )}
          </div>
        )
      )}
    </li>
  );
};

type Props = {
  surveyId: number;
  risks: ResidualRisk[];
  choices: ResidualChoices;
  canDecide: boolean;
};

/** Every area that is still high or has become worse gets a decision before the work is archived */
export const ResidualRiskCard = ({
  surveyId,
  risks,
  choices,
  canDecide,
}: Props): ReactElement => {
  const { t } = useTranslation();
  return (
    <section
      id={RESIDUAL_SECTION_ID}
      aria-labelledby="residual-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="residual-heading">
            {t('workEnvironmentArchive.residual.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentArchive.residual.intro')}
          </Paragraph>
        </div>
        {risks.length === 0 ? (
          <Paragraph data-size="sm">
            {t('workEnvironmentArchive.residual.none')}
          </Paragraph>
        ) : (
          <>
            <Paragraph
              data-size="sm"
              role="status"
              className={classes.progress}
            >
              {t(
                'workEnvironmentArchive.residual.progress',
                residualProgress(risks),
              )}
            </Paragraph>
            <ul className={classes.residualList}>
              {risks.map((risk) => (
                <ResidualItem
                  key={risk.key}
                  surveyId={surveyId}
                  risk={risk}
                  choices={choices}
                  canDecide={canDecide}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
};
